import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { App as AntdApp } from 'antd';
import { store, useAppSelector } from './store/index';
import { ApiTestPanel } from './components/debug/ApiTestPanel';
import { EmeraldThemeProvider } from './theme/EmeraldThemeProvider';
import MainLayout from './components/layout/MainLayout';

// 全局响应式修复样式
import './styles/responsive.css';
import './styles/responsive-fix.css';
import './styles/responsive-critical-fix.css';
// ✅ 登录页面已重构为Ant Design Grid，不再需要专用CSS
import './styles/screening-workbench-fix.css';

// StatusCard暂时未使用，需要时再导入
// import { StatusCard } from './components/ui/StatusCard';

// 导入认证页面
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ActivateAccountPage from './pages/auth/ActivateAccountPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// 导入系统设置页面
import EmailConfigPage from './pages/system-settings/EmailConfigPage';

// 导入敬请期待页面
import ComingSoonPage from './pages/ComingSoonPage';

// 导入问卷组件
import InitialScreeningQuestionnaireComponent from './components/questionnaire/InitialScreeningQuestionnaire';
import QuestionnaireStatusPage from './pages/questionnaire/QuestionnaireStatusPage';

// 删除不需要的页面导入

// 导入筛选工作台
import ScreeningWorkbench from './pages/ScreeningWorkbench';

// 导入项目管理模块
import IssuerDashboard from './pages/project-management/IssuerDashboard';
import ProjectDetail from './pages/project-management/ProjectDetail';
import IssuerWorkbench from './pages/project-management/IssuerWorkbench';

// 导入发行人问卷管理模块
import IssuerQuestionnaireList from './pages/issuer/IssuerQuestionnaireList';

// 导入渠道管理模块
import ChannelManagement from './pages/channel-management/ChannelManagement';

// 导入用户管理模块
import UserManagement from './pages/user-management/UserManagement';

// 导入主题测试页面
import ThemeTestPage from './pages/ThemeTestPage';

// 导入详细评估模块
import OptimizedDetailedAssessment from './pages/detailed-assessment/OptimizedDetailedAssessment';
import DetailedAssessmentReview from './pages/detailed-assessment/DetailedAssessmentReview';
import UnifiedWorkflowPage from './pages/project-management/UnifiedWorkflowPage';
import ExpertWorkflowPage from './pages/project-management/ExpertWorkflowPage';

// 导入权限控制组件
import RoleBasedRoute from './components/auth/RoleBasedRoute';
import { UserRole } from './store/slices/authSlice';
import { getDefaultRoute } from './config/permissions';

// 基础路由保护组件（只检查登录状态）
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAppSelector(state => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  return <>{children}</>;
};

// 智能首页重定向组件
const SmartHomeRedirect: React.FC = () => {
  const { user } = useAppSelector(state => state.auth);

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  const defaultRoute = getDefaultRoute(user.role as UserRole);
  return <Navigate to={defaultRoute} replace />;
};

// 创建 React Query 客户端
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// 页面切换时自动滚动到顶部
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  return (
    <Provider store={store}>
      <EmeraldThemeProvider>
        <AntdApp>
          <QueryClientProvider client={queryClient}>
            <Router>
              <ScrollToTop />
              <Routes>
                {/* 认证相关路由 */}
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/register" element={<RegisterPage />} />
                <Route
                  path="/auth/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path="/auth/reset-password"
                  element={<ResetPasswordPage />}
                />
                <Route
                  path="/auth/activate"
                  element={<ActivateAccountPage />}
                />

                {/* 公开页面路由 */}
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />

                {/* 问卷路由 - 无需登录 */}
                <Route
                  path="/questionnaire"
                  element={<InitialScreeningQuestionnaireComponent />}
                />
                <Route
                  path="/questionnaire/status"
                  element={<QuestionnaireStatusPage />}
                />

                {/* 重定向旧的登录路由 */}
                <Route
                  path="/login"
                  element={<Navigate to="/auth/login" replace />}
                />

                {/* 受保护的应用路由 - 统一使用admin前缀 */}
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Routes>
                          {/* 智能首页重定向 */}
                          <Route index element={<SmartHomeRedirect />} />

                          {/* 筛选工作台 - 只有交易所和管理员可以访问 */}
                          <Route
                            path="screening"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.EXCHANGE,
                                  UserRole.ADMIN,
                                ]}
                              >
                                <ScreeningWorkbench />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 项目管理相关 - 发行人、交易所和管理员 */}
                          <Route
                            path="project-management/*"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ISSUER,
                                  UserRole.EXCHANGE,
                                  UserRole.ADMIN,
                                ]}
                              >
                                <Routes>
                                  <Route index element={<IssuerDashboard />} />
                                  <Route
                                    path="issuer"
                                    element={<IssuerDashboard />}
                                  />
                                  <Route
                                    path="workbench/:projectId"
                                    element={<IssuerWorkbench />}
                                  />
                                  <Route
                                    path="projects/:projectCode"
                                    element={<ProjectDetail />}
                                  />
                                  <Route
                                    path="projects/:projectCode/workflow"
                                    element={<UnifiedWorkflowPage />}
                                  />
                                  <Route
                                    path="projects/:projectCode/expert-workflow"
                                    element={<ExpertWorkflowPage />}
                                  />
                                  <Route
                                    path="projects/:projectCode/detailed-assessment"
                                    element={<OptimizedDetailedAssessment />}
                                  />
                                  <Route
                                    path="projects/:projectCode/detailed-assessment/review"
                                    element={<DetailedAssessmentReview />}
                                  />
                                </Routes>
                              </RoleBasedRoute>
                            }
                          />

                          {/* 项目管理主页面 - 发行人、交易所和管理员 */}
                          <Route
                            path="project-management"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ISSUER,
                                  UserRole.EXCHANGE,
                                  UserRole.ADMIN,
                                ]}
                              >
                                <IssuerDashboard />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 发行人工作台 - 发行人、交易所和管理员 */}
                          <Route
                            path="issuer-workbench/:projectId"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ISSUER,
                                  UserRole.EXCHANGE,
                                  UserRole.ADMIN,
                                ]}
                              >
                                <IssuerWorkbench />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 发行人问卷管理 - 发行人、交易所和管理员 */}
                          <Route
                            path="issuer/questionnaires"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ISSUER,
                                  UserRole.EXCHANGE,
                                  UserRole.ADMIN,
                                ]}
                              >
                                <IssuerQuestionnaireList />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 渠道管理 - 交易所和管理员 */}
                          <Route
                            path="channel-management"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.EXCHANGE,
                                  UserRole.ADMIN,
                                ]}
                              >
                                <ChannelManagement />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 用户管理 - 管理员和交易所 */}
                          <Route
                            path="user-management"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ADMIN,
                                  UserRole.EXCHANGE,
                                ]}
                              >
                                <UserManagement />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 系统设置 - 邮件配置 - 仅管理员 */}
                          <Route
                            path="system-settings/email"
                            element={
                              <RoleBasedRoute allowedRoles={[UserRole.ADMIN]}>
                                <EmailConfigPage />
                              </RoleBasedRoute>
                            }
                          />

                          {/* 主题测试页面 - 开发调试用 */}
                          <Route
                            path="theme-test"
                            element={<ThemeTestPage />}
                          />

                          {/* 数据分析 - 敬请期待 */}
                          <Route
                            path="analytics/dashboard"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ADMIN,
                                  UserRole.EXCHANGE,
                                ]}
                              >
                                <ComingSoonPage />
                              </RoleBasedRoute>
                            }
                          />
                          <Route
                            path="analytics/reports"
                            element={
                              <RoleBasedRoute
                                allowedRoles={[
                                  UserRole.ADMIN,
                                  UserRole.EXCHANGE,
                                ]}
                              >
                                <ComingSoonPage />
                              </RoleBasedRoute>
                            }
                          />

                          {/* API测试面板 - 开发环境，所有角色 */}
                          {process.env.NODE_ENV === 'development' && (
                            <Route
                              path="api-test"
                              element={
                                <RoleBasedRoute
                                  allowedRoles={[
                                    UserRole.ADMIN,
                                    UserRole.EXCHANGE,
                                    UserRole.ISSUER,
                                  ]}
                                >
                                  <ApiTestPanel />
                                </RoleBasedRoute>
                              }
                            />
                          )}

                          {/* 404页面或其他通用路由 */}
                          <Route path="*" element={<SmartHomeRedirect />} />
                        </Routes>
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* 根路径重定向到admin */}
                <Route path="/" element={<SmartHomeRedirect />} />
              </Routes>
            </Router>
          </QueryClientProvider>
        </AntdApp>
      </EmeraldThemeProvider>
    </Provider>
  );
}

export default App;
